import { Grid, Skeleton, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs } from "@mui/material";
import { useState, useEffect } from "react";
import { ServiceApi } from "../../../services/index.service";
import { useSelector } from "react-redux";
import moment from "moment";
import { useSnackbar } from "notistack";

// Define alternating row colors
const rowColors = ['#FFF8F8', '#ffffff'];
// Function to get the background color for each row
const getRowColor = (index) => {
    return rowColors[index % rowColors.length];
};

//CSS for Table
const tableReportCSS = {
    tableCellCSS: {
        border: '1px solid #D2D2D2',
    },
    tableRowCSS: (rowIndex) => {
        return {
            'backgroundColor': getRowColor(rowIndex)
        }
    },
    tableOuterBorderCollapse: {
        borderCollapse: 'collapse',
        border: '1px solid #D2D2D2'
    }
}

const SkeletonLoading = ({ tabValue }) => (<div className="bg-white p-4" >
    {tabValue === 0 && <div className="flex">
        {[...Array(1).keys()].map((i) => <Skeleton key={i} variant="text" className="text-8xl w-56 mx-2 -mt-4" />)}
    </div>}
    <table className="w-full">
        <tbody>
            {[...Array(tabValue === 1 ? 8 : 6).keys()].map((i) => <tr key={i}>
                {[...Array(tabValue === 1 ? 4 : 6).keys()].map((i) => <td key={i} width={100} className="px-3" >
                    <Skeleton variant="text" sx={{ fontSize: '1.5rem' }} />
                </td>)}
            </tr>)}
        </tbody>
    </table>
</div>);

const reportTabs = [
    { label: 'Cool Alerts Report', value: 'COOL_ALERTS_REPORT' },
    { label: 'Opt Out Report', value: 'OPT_OUT_REPORT' },
    { label: 'Delivery Report', value: 'DELIVERY_REPORT' }
];

const OptOutHeaders = [
    { label: "Category", key: 'category' },
    { label: "Flavor Name", key: 'flavorName' },
    { label: "# Unique Subscribed", key: 'uniqueSubCount' },
    { label: "# OptOut Email", key: 'optOutEmailCount' },
];
const CoolAlertsHeaders = [
    { label: "Category", key: 'category', rowSpan: 2 },
    { label: "Flavor Name", key: 'flavorName', rowSpan: 2 },
    { label: "Store % Favorite", key: 'storeFavorite', rowSpan: 2, valueFormatter: (val) => `${val}%` },
    { label: "Last used", key: 'daysAgo', rowSpan: 2, valueFormatter: (days) => days === 0 ? 'today' : moment().subtract(days, 'days').fromNow() },
    { label: "# Times Flavor Used", colSpan: 4, align: 'center' },
    { label: "Season Opening", key: "seasonal", grouping: true },
    { label: "MTD", key: "monthly", grouping: true },
    { label: "YTD", key: "yearly", grouping: true },
    { label: "Previous Year", key: "previous", grouping: true }
];

const DeliveryHeaders = [
    { label: "Date", key: 'date' },
    { label: "Number of Emails Created (In Last 30days)", key: 'emails_created' }
];

const Reports = () => {
    const serviceapi = new ServiceApi();
    const { enqueueSnackbar } = useSnackbar();
    const { storage } = useSelector((state) => state);
    const [reportData, setReportData] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [tableHeaders, setTableHeaders] = useState([...OptOutHeaders]);
    const [totalOptoutValues, setTotalOptoutValues] = useState({ unique: 0, optout: 0 });
    const [totalEnrollments, setTotalEnrollments] = useState({
        unique: 0,
        email: 0
    });

    useEffect(() => {
        if (storage?.store?.intStoreID)
            getReportsData();
    }, [storage?.store?.intStoreID, tabValue]);

    const getReportsData = async () => {
        setLoading(true);
        try {
            let response = [];
            if (tabValue === 1) {
                const { statusCode, optOutData, uniqueTotalCount, optoutTotalCount } = await serviceapi.getCoolAlertsReportData(storage?.store?.intStoreID, "opt_out");
                if (statusCode === 200) response = optOutData;
                else response = [];
                setTableHeaders(OptOutHeaders);
                setTotalOptoutValues({ unique: uniqueTotalCount || 0, optout: optoutTotalCount || 0 });
                setTotalEnrollments({ unique: 0, email: 0 });
            }
            else if (tabValue === 0) {
                const { statusCode, flavorsData, totalCount, emailCount } = await serviceapi.getCoolAlertsReportData(storage?.store?.intStoreID, "flavors");
                if (statusCode === 200) {
                    response = flavorsData;
                    setTotalEnrollments({ unique: totalCount || 0, email: emailCount || 0 });
                }
                else {
                    response = [];
                    setTotalEnrollments({ unique: 0, email: 0 });
                };
                setTotalOptoutValues({ unique: 0, optout: 0 });
                setTableHeaders(CoolAlertsHeaders);
            }
            else if (tabValue === 2) {
                const { statusCode, data } = await serviceapi.getCoolAlertsReportData(storage?.store?.intStoreID, "delivery");
                if (statusCode === 200) response = data;
                else response = [];
                setTableHeaders(DeliveryHeaders);
                setTotalEnrollments({ unique: 0, email: 0 });
                setTotalOptoutValues({ unique: 0, optout: 0 });
            }
            setReportData(response);
        } catch (err) {
            console.log(err);
            enqueueSnackbar(err?.response?.data?.message || 'Something went wrong, please try again!', {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error"
            });
            setReportData([]);
        }
        setLoading(false);
    }

    return (
        <>
            <Tabs
                value={tabValue}
                onChange={(event, val) => setTabValue(val)}
                className="border-b border-primary"
                TabIndicatorProps={{ style: { display: "none" } }}
            >
                {reportTabs.map((tab, i) => <Tab
                    className="capitalize text-right bg-white text-primary mr-2 rounded-t-md text-xs"
                    key={i}
                    label={tab.label}
                    id={tab.value}
                    tabIndex={i}
                    classes={{ selected: "!text-white !bg-primary" }}
                    disabled={loading}
                />)}
            </Tabs>
            {loading ?
                <SkeletonLoading tabValue={tabValue} />
                :
                <div item xs={12} className="p-5 bg-white">
                    {tabValue === 0 && <Grid container spacing={3} className="p-6">
                        <Grid item sm={"auto"} className="w-60 flex justify-between items-center border border-[#D2D2D2] px-4 py-1.5 rounded-md">
                            <h2 className="text-sm">Total Email Unique <br /> Enrollment</h2>
                            <h1 className="ml-4 text-2xl font-semibold text-primary">{totalEnrollments.email}</h1>
                        </Grid>
                    </Grid>}
                    {reportData?.length > 0 ? <div className="overflow-x-auto">
                        <Table aria-label="Optout table" sx={tableReportCSS.tableOuterBorderCollapse}>
                            <TableHead>
                                <TableRow className="bg-[#484848]">
                                    {tableHeaders.filter(h => !h.grouping).map(({ label, rowSpan = 1, colSpan = 1, align = "left" }, i) =>
                                        <TableCell
                                            key={i}
                                            className="text-white py-2"
                                            sx={{ border: '1px solid #D2D2D2' }}
                                            rowSpan={rowSpan}
                                            colSpan={colSpan}
                                            align={align}
                                        >
                                            {label}
                                        </TableCell>
                                    )}
                                </TableRow>
                                {
                                    tabValue === 0 && <TableRow className="bg-[#484848]">
                                        {tableHeaders.filter(h => h.grouping).map(({ label }, i) =>
                                            <TableCell
                                                key={i}
                                                className="text-white py-2"
                                                sx={{ border: '1px solid #D2D2D2' }}
                                            >
                                                {label}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                }
                            </TableHead>
                            <TableBody>
                                {reportData.map((row, rowIndex) =>
                                    <TableRow key={rowIndex} sx={tableReportCSS.tableRowCSS(rowIndex)} >
                                        {tableHeaders.filter(h => h.key).map(({ key, valueFormatter }, i) =>
                                            <TableCell key={i} sx={tableReportCSS.tableCellCSS} className={`py-2.5 ${key === "flavorName" && 'max-w-[225px] '}`}>
                                                {valueFormatter ? valueFormatter(row[key]) : row[key]}
                                            </TableCell>)}
                                    </TableRow>
                                )}
                                {tabValue === 1 &&
                                    <TableRow sx={tableReportCSS.tableRowCSS(reportData.length)}>
                                        <TableCell align="center" colSpan={2} className="font-semibold">Total</TableCell>
                                        <TableCell sx={tableReportCSS.tableCellCSS} className={`py-2.5 font-semibold `}>{totalOptoutValues?.unique}</TableCell>
                                        <TableCell sx={tableReportCSS.tableCellCSS} className={`py-2.5 font-semibold `}>{totalOptoutValues?.optout}</TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </div>
                        : <p className="my-5 text-center">No Reports found.</p>}
                </div>
            }
        </>
    )
}

export default Reports;